import { create } from 'zustand';

import { adoptAccountLocale } from '@/shared/i18n';
import { ApiError } from '@/shared/services/api/api-error';
import { httpClient } from '@/shared/services/api/http-client';
import type { AuthTokens, Me, PermissionRule } from '@/shared/services/api/types';
import { NO_PERMISSIONS, PermissionSet } from '@/shared/services/permissions/permission-set';
import { deviceStorage } from '@/shared/services/storage/device-storage';
import { diskStore } from '@/shared/services/storage/disk-store';
import type { AppMode } from '@/stores/app-mode';

/** L'espace choisi (acheteur ou vendeur), retrouvé au rechargement. */
const MODE_KEY = 'bul.mode';

function storedMode(): AppMode | undefined {
  const value = deviceStorage.readNow(MODE_KEY);
  return value === 'buyer' || value === 'seller' ? value : undefined;
}

/**
 * Session courante : qui est connecté, et ce qu'il a le droit de faire.
 *
 * Le compte et ses permissions arrivent ensemble : l'interface ne se dessine
 * jamais a partir du rôle seul. `mode` désigne l'ESPACE affiché — chaque
 * métier du cahier des charges a le sien :
 *
 *  - `buyer` / `seller` : les deux espaces du compte marchand (B.2), une
 *    préférence d'affichage que la bascule de « Mon espace » change ;
 *  - `admin` (B.4) et `agent` (B.3) : l'espace unique de ces comptes. Un
 *    administrateur administre, un certificateur inspecte : ni fil, ni panier,
 *    et pas de bascule. C'est ici le seul endroit du mobile ou le rôle est lu,
 *    comme le POLICY_REGISTRY est le seul du serveur : l'espace est la
 *    présentation d'un métier, les droits restent affaire de permissions.
 */
export type { AppMode };

/**
 * Racine de chaque espace : le premier écran du métier. C'est la que les
 * gardes de route renvoient un espace qui n'a rien a faire sur un onglet
 * (le panier en mode pro, le fil en mode administration).
 */
export const SPACE_ROOTS: Record<AppMode, string> = {
  buyer: '/',
  seller: '/seller',
  admin: '/admin',
  agent: '/agent/missions',
};

/** L'espace d'un compte. `previous` garde le choix acheteur/vendeur en cours. */
function resolveMode(me: Me, previous?: AppMode): AppMode {
  if (me.role === 'ADMIN') {
    return 'admin';
  }
  if (me.role === 'CERTIFIER') {
    return 'agent';
  }
  // L'espace pro appartient a qui OPERE une boutique : le responsable, et
  // l'aide vendeur dont l'adhésion est approuvée (décision 6). Le lire sur les
  // seules boutiques possédées reprenait son outil au sous-vendeur a chaque
  // rechargement de session.
  if (me.ownedShopIds.length === 0 && me.memberShopIds.length === 0) {
    return 'buyer';
  }
  // Qui tient une boutique ouvre sur son outil, qu'elle soit la sienne ou celle
  // d'une équipe qu'il a rejointe : on entre dans l'application pour
  // travailler. S'il avait choisi le mode acheteur, on ne le lui reprend pas.
  return previous === 'buyer' ? 'buyer' : 'seller';
}

/**
 * Instantané de session sur disque : le compte et ses regles de permission,
 * tels que le serveur les a donnés la derniere fois qu'il a répondu.
 *
 * C'est lui qui ouvre l'application sans réseau. Avant lui, le démarrage
 * appelait le serveur en bloquant l'écran de lancement (plusieurs secondes de
 * logo sur un réseau lent), et une simple panne réseau **jetait les jetons** :
 * ouvrir l'application dans un tunnel déconnectait le compte. Désormais, la
 * session connue s'affiche tout de suite ; le serveur ne fait que la remettre
 * a jour quand il répond, et lui seul peut la déclarer finie.
 */
const SNAPSHOT_FILE = 'session-snapshot.json';

type SessionSnapshot = { me: Me; rules: Array<PermissionRule> };

function saveSnapshot(me: Me, rules: Array<PermissionRule>): void {
  try {
    diskStore.write(SNAPSHOT_FILE, JSON.stringify({ me, rules } satisfies SessionSnapshot));
  } catch {
    // Sans instantané, le prochain démarrage repassera par le réseau : rien de cassé.
  }
}

async function readSnapshot(): Promise<SessionSnapshot | null> {
  const raw = await diskStore.read(SNAPSHOT_FILE);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<SessionSnapshot>;
    return parsed.me && Array.isArray(parsed.rules) ? (parsed as SessionSnapshot) : null;
  } catch {
    return null;
  }
}

/**
 * La langue du compte, telle que le serveur la connait.
 *
 * Elle ne s'impose qu'a qui n'a jamais choisi sur CET appareil (voir
 * `adoptAccountLocale`) : c'est ce qui fait qu'un vendeur retrouve le malgache
 * sur un téléphone neuf, sans jamais reprendre a quelqu'un le choix qu'il vient
 * de faire ici. Rien ne l'attend : la langue posée est déja la bonne dans la
 * quasi-totalité des cas, et l'écran n'a pas a patienter pour l'exception.
 */
function followAccountLanguage(me: Me): void {
  void adoptAccountLocale(me.language);
}

/**
 * Remise a jour de la session restaurée, en arriere-plan du premier écran.
 *
 * Un 403 dit une session finie (compte suspendu ou bloqué) : on sort. Un 401
 * qui perce jusqu'ici est déja passé par la rotation du jeton, et son refus a
 * déja déclenché `onSessionLost` : rien a refaire. Tout le reste, panne
 * réseau ou serveur a terre, laisse la session connue en place : dégrader les
 * droits affichés parce qu'un tunnel passait par la serait une régression.
 */
async function revalidateSession(): Promise<void> {
  try {
    const [me, permissions] = await Promise.all([
      httpClient.get<Me>('/auth/me'),
      httpClient.get<{ rules: Array<PermissionRule> }>('/auth/me/permissions'),
    ]);
    useSessionStore.getState().refreshMe(me, permissions.rules);
  } catch (error) {
    if (error instanceof ApiError && error.isForbidden) {
      void useSessionStore.getState().signOut();
    }
  }
}

interface SessionState {
  /** `true` tant qu'on n'a pas fini de relire le stockage sécurisé au démarrage. */
  loading: boolean;
  me: Me | null;
  permissions: PermissionSet;
  mode: AppMode;

  restore: () => Promise<void>;
  signIn: (tokens: AuthTokens) => Promise<void>;
  signOut: () => Promise<void>;
  refreshMe: (me: Me, rules: Array<PermissionRule>) => void;
  setMode: (mode: AppMode) => void;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  loading: true,
  me: null,
  permissions: NO_PERMISSIONS,
  mode: 'buyer',

  async restore() {
    const stored = await httpClient.restore();
    if (!stored) {
      // Pas de jetons : l'instantané éventuel est celui d'une session finie.
      diskStore.remove(SNAPSHOT_FILE);
      set({ loading: false, me: null, permissions: NO_PERMISSIONS });
      return;
    }

    const snapshot = await readSnapshot();
    if (snapshot) {
      followAccountLanguage(snapshot.me);
      // La session connue s'affiche sans attendre le réseau : c'est ce qui
      // ouvre l'application dans un tunnel comme dans un salon, et l'écran de
      // lancement ne dure plus que le temps d'une lecture disque.
      set({
        loading: false,
        me: snapshot.me,
        permissions: new PermissionSet(snapshot.rules),
        // Chacun retrouve son espace : l'outil pour un responsable, les
        // missions pour un agent, l'administration pour un administrateur.
        mode: resolveMode(snapshot.me, storedMode()),
      });
      void revalidateSession();
      return;
    }

    // Des jetons sans instantané (premiere ouverture depuis cette version) :
    // l'ancien chemin, le réseau avant l'écran, une seule fois.
    try {
      const [me, permissions] = await Promise.all([
        httpClient.get<Me>('/auth/me'),
        httpClient.get<{ rules: Array<PermissionRule> }>('/auth/me/permissions'),
      ]);
      saveSnapshot(me, permissions.rules);
      followAccountLanguage(me);
      set({
        loading: false,
        me,
        permissions: new PermissionSet(permissions.rules),
        mode: resolveMode(me, storedMode()),
      });
    } catch (error) {
      // Seul un refus du serveur (session révoquée, compte bloqué) jette les
      // jetons. Une panne réseau les garde : conclure « session morte » parce
      // qu'un tunnel passait par la déconnectait des comptes bien vivants.
      if (error instanceof ApiError && (error.isUnauthorized || error.isForbidden)) {
        await httpClient.clearTokens();
      }
      set({ loading: false, me: null, permissions: NO_PERMISSIONS });
    }
  },

  async signIn(tokens) {
    // Les jetons d'abord, les permissions ensuite : `/auth/me/permissions` est
    // une route authentifiée ; dans l'autre sens, l'appel partirait sans jeton
    // et le serveur répondrait « Jeton manquant ». La séquence vit ici, pas
    // dans les écrans, pour qu'aucun appelant ne puisse se tromper d'ordre.
    await httpClient.setTokens({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    });
    const permissions = await httpClient.get<{ rules: Array<PermissionRule> }>(
      '/auth/me/permissions',
    );
    saveSnapshot(tokens.user, permissions.rules);
    followAccountLanguage(tokens.user);
    set({
      loading: false,
      me: tokens.user,
      permissions: new PermissionSet(permissions.rules),
      mode: resolveMode(tokens.user),
    });
  },

  async signOut() {
    // Le site n'enrôle aucun téléphone (pas de notification système côté web) :
    // fermer la session suffit, les autres appareils du compte ne sont pas
    // touchés.
    try {
      // `endsSession` : un 401 est ici l'issue normale, la session qu'on ferme
      // peut déja avoir été révoquée par l'administration. Sans ce drapeau,
      // l'échec relançait la rotation, qui concluait « session perdue », qui
      // rappelait cette méthode : une boucle sans fin de requetes.
      await httpClient.post('/auth/logout', undefined, { endsSession: true });
    } catch {
      // La session locale part de toute facon : un serveur injoignable ne doit
      // pas retenir l'utilisateur dans l'application.
    }
    await httpClient.clearTokens();
    diskStore.remove(SNAPSHOT_FILE);
    set({ me: null, permissions: NO_PERMISSIONS, mode: 'buyer', loading: false });
    void deviceStorage.remove(MODE_KEY);
  },

  refreshMe(me, rules) {
    const previous = get().mode;
    saveSnapshot(me, rules);
    followAccountLanguage(me);
    set({
      me,
      permissions: new PermissionSet(rules),
      // Le choix acheteur/vendeur en cours est respecté ; un rôle qui change
      // (candidature d'agent approuvée, par exemple) emmene vers son espace.
      mode: resolveMode(me, previous),
    });
  },

  setMode(mode) {
    // Un espace ne se choisit que la ou il y a un choix : l'administrateur et
    // l'agent n'ont que le leur, aucun geste ne les en sort.
    const me = get().me;
    if (me && (me.role === 'ADMIN' || me.role === 'CERTIFIER')) {
      return;
    }
    set({ mode });
    // Sur le web, l'espace choisi survit au rechargement de la page : un
    // vendeur qui parcourt la place de marché ne se retrouve pas renvoyé à son
    // tableau de bord à chaque actualisation.
    void deviceStorage.write(MODE_KEY, mode);
  },
}));

/** Raccourcis de lecture, pour éviter de réécrire le sélecteur partout. */
export const useMe = (): Me | null => useSessionStore((state) => state.me);
export const usePermissions = (): PermissionSet => useSessionStore((state) => state.permissions);
export const useIsSignedIn = (): boolean => useSessionStore((state) => state.me !== null);
export const useAppMode = (): AppMode => useSessionStore((state) => state.mode);
